import React from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import {
  CurrencyType,
  RefundAttemptDto,
  SettledAmountDto,
} from '@tiendanube/common';
import AmountDto from '@tiendanube/common/dist/src/domains/payments/refund/AmountDto';
import { Domain } from '@tiendanube/common/src/enums';
import { DataList } from '@tiendanube/components';
import { CurrencyPrice } from 'commons/components';
import { SettledAmount } from 'domains/Orders/components';
import {
  useRefundCalculations,
  useCrossBorderCalculations,
} from 'domains/Orders/Orders/pages/hooks';

interface Attributes {
  isEdited: boolean;
  total: number;
  currency: CurrencyType;
  refundAttempts: RefundAttemptDto[];
  merchantChargesTotalAmount?: AmountDto;
  refundedMerchantChargesTotalAmount?: AmountDto;
  settledAmount?: SettledAmountDto;
  orderHasSubtotal: boolean;
}

function OrderTotalAmountRow({
  isEdited,
  total,
  refundAttempts,
  currency,
  merchantChargesTotalAmount,
  refundedMerchantChargesTotalAmount,
  settledAmount,
  orderHasSubtotal,
}: Attributes): React.JSX.Element {
  const { t } = useTranslation(Domain.ORDERS, {
    keyPrefix: 'orderSummaryCard.tableAmounts',
  });
  const { calculateRefundedAmount } = useRefundCalculations(refundAttempts);
  const refundedAmount = calculateRefundedAmount(currency);

  const { getPrimaryTotal, showSettledAsSecondaryInTotal } =
    useCrossBorderCalculations(
      total,
      currency,
      settledAmount,
      orderHasSubtotal,
    );
  const primaryTotal = getPrimaryTotal();

  const getTotal = () => {
    if (isEdited) {
      return Number(primaryTotal.value);
    }

    return (
      Number(primaryTotal.value) -
      Number(merchantChargesTotalAmount?.value ?? '0.00') -
      Number(refundedAmount.value) +
      Number(refundedMerchantChargesTotalAmount?.value ?? '0.00')
    );
  };

  return (
    <DataList.Row id="total" grayed>
      <DataList.Cell width={70}>
        <Box display="flex" flexDirection="row" gap="0-5">
          <Text fontWeight="bold" color="neutral-textHigh">
            {isEdited ? t('updatedTotal') : t('total')}
          </Text>
        </Box>
      </DataList.Cell>
      <DataList.Cell width={30}>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <CurrencyPrice
            price={getTotal()}
            currency={primaryTotal.currency}
            block
            textAlign="right"
            bold
          />
          {settledAmount && showSettledAsSecondaryInTotal() && (
            <SettledAmount amount={settledAmount} />
          )}
        </Box>
      </DataList.Cell>
    </DataList.Row>
  );
}
export default OrderTotalAmountRow;
